// ── Scout — Composition Algorithm ──────────────────────────────
// Flow:
//   1. Member joins waitlist with preferences (type, availability)
//   2. Algo runs periodically — finds valid groups from the waitlist pool
//   3. Valid group = 4–8 people, ≥3 shared interests, gender parity ±1, age ±5y
//   4. Group receives a proposal: date (1 week out), time, venue
//   5. Each member has 48h to confirm — minimum 4 confirmations to hold event
//   6. Post-Scout: anonymous vote → mutual matches revealed with compat scores

import { CATEGORY_TREE_DEF, getAllLeaves } from './categoryTree';

// ── Scout types ──────────────────────────────────────────────────────────
export const SCOUT_TYPES = {
  dinner:      { id:'dinner',      labelKey:'scout.type_dinner',      icon:'🍽️', size:[6,6],   romantic:true,  dateType:false },
  apero:       { id:'apero',       labelKey:'scout.type_apero',       icon:'🍷', size:[6,6],   romantic:true,  dateType:false },
  coffee:      { id:'coffee',      labelKey:'scout.type_coffee',      icon:'☕', size:[4,5],   romantic:false, dateType:false },
  outdoor:     { id:'outdoor',     labelKey:'scout.type_outdoor',     icon:'🌲', size:[4,6],   romantic:false, dateType:false },
  culture:     { id:'culture',     labelKey:'scout.type_culture',     icon:'🎭', size:[4,6],   romantic:false, dateType:false },
  board_games: { id:'board_games', labelKey:'scout.type_board_games', icon:'🎲', size:[4,6],   romantic:false, dateType:false },
  sport:       { id:'sport',       labelKey:'scout.type_sport',       icon:'🏃', size:[4,8],   romantic:false, dateType:false },
  workshop:    { id:'workshop',    labelKey:'scout.type_workshop',    icon:'🎨', size:[4,8],   romantic:false, dateType:false },
  date:        { id:'date',        labelKey:'scout.type_date',        icon:'💫', size:[2,2],   romantic:true,  dateType:true  },
  mixer:       { id:'mixer',       labelKey:'scout.type_mixer',       icon:'✨', size:[6,10],  romantic:true,  dateType:false },
};

// ── Mock venues by city ───────────────────────────────────────────────────
export const SCOUT_VENUES = {
  ca_montreal: {
    dinner:   [
      { name:'Café Resonance',    address:'5175 Ave du Parc, Montréal',  area:'Mile-End' },
      { name:'Bar Le Lab',        address:'1351 Rachel Est, Montréal',    area:'Plateau'  },
      { name:'Tabac & Co',        address:'2020 Rue Peel, Montréal',      area:'Centre-ville' },
    ],
    outdoor:  [
      { name:'Parc La Fontaine',  address:'Entrée Papineau, Montréal',   area:'Plateau'  },
      { name:'Mont-Royal',        address:'Voie Camillien-Houde',        area:'Mont-Royal' },
    ],
    workshop: [
      { name:'Espace Créatif MTL',address:'4115 Rue Saint-Denis',        area:'Plateau'  },
      { name:'Atelier Poterie Co',address:'60 Ave Laurier O.',           area:'Outremont' },
    ],
    apero:    [{ name:'Chez l\'hôte',  address:'Communiqué aux participants', area:'Variable' }],
  },
  paris: {
    dinner:   [{ name:'Le Petit Zinc',  address:'11 Rue Saint-Benoît, Paris 6', area:'Saint-Germain' }],
    outdoor:  [{ name:'Bois de Vincennes', address:'Entrée Porte Dorée',       area:'12e'           }],
    workshop: [{ name:'Atelier des Sens', address:'40 Rue du Faubourg Poissonnière', area:'10e'      }],
    apero:    [{ name:'Chez l\'hôte',   address:'Communiqué aux participants',  area:'Variable'      }],
  },
  lorient: {
    dinner:   [{ name:'Le Jardin',      address:'3 Rue de la Patrie, Lorient',  area:'Centre'        }],
    outdoor:  [{ name:'Plage de Larmor',address:'Larmor-Plage',                area:'Bord de mer'   }],
    workshop: [{ name:'L\'Atelier',     address:'15 Rue du Port, Lorient',      area:'Centre'        }],
    apero:    [{ name:'Chez l\'hôte',   address:'Communiqué aux participants',  area:'Variable'      }],
  },
};

// ── Get shared interests between two users ────────────────────────────────
export function sharedInterests(userA, userB) {
  const a = new Set(userA.interests || []);
  const b = new Set(userB.interests || []);
  return [...b].filter(i => a.has(i));
}

// ── Score a group for Scout compatibility ────────────────────────────────
export function scoreGroup(members) {
  if (members.length < 2) return { score: 0, sharedInterests: [], valid: false };

  // Find interests shared by ALL members
  const interestSets = members.map(m => new Set(m.interests || []));
  const allInterests = [...(interestSets[0] || new Set())];
  const common = allInterests.filter(i => interestSets.every(s => s.has(i)));

  // Average pairwise quiz compat
  let compatSum = 0, pairs = 0;
  for (let i = 0; i < members.length; i++) {
    for (let j = i + 1; j < members.length; j++) {
      compatSum += pairwiseCompat(members[i], members[j]);
      pairs++;
    }
  }
  const avgCompat = pairs > 0 ? compatSum / pairs : 0;

  // Gender parity check
  const genderCounts = {};
  members.forEach(m => { genderCounts[m.gender] = (genderCounts[m.gender] || 0) + 1; });
  const counts = Object.values(genderCounts);
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);
  // Require at least 2 genders represented AND no gender outnumbers another by more than 1
  const parityOk = counts.length >= 2 && (maxCount - minCount) <= 1;

  // Age spread check
  const ages = members.map(m => m.age || 28);
  const ageSpread = Math.max(...ages) - Math.min(...ages);
  const ageOk = ageSpread <= 10; // ±5y = max spread of 10

  const valid = common.length >= 3 && avgCompat >= 0.55 && parityOk && ageOk;
  const score = (common.length * 10) + (avgCompat * 50) + (parityOk ? 20 : 0) + (ageOk ? 10 : 0);

  return { score, sharedInterests: common, avgCompat, parityOk, ageOk, valid, genderCounts };
}

function pairwiseCompat(a, b) {
  const axes = ['narrative','stability','boredom','social','stakes','repair','conflict','accountability','generosity','presence'];
  const aScores = a.criteria_scores || a.quiz_answers || {};
  const bScores = b.criteria_scores || b.quiz_answers || {};
  let sum = 0, count = 0;
  axes.forEach(ax => {
    const av = (aScores[ax] || 5) / 10;
    const bv = (bScores[ax] || 5) / 10;
    sum += 1 - Math.abs(av - bv);
    count++;
  });
  return count > 0 ? sum / count : 0.6;
}

// ── Build a Scout proposal ───────────────────────────────────────────────
export function buildScoutProposal({ members, type, network, id }) {
  const scoutType = SCOUT_TYPES[type] || SCOUT_TYPES.dinner;
  const venues = SCOUT_VENUES[network]?.[type] || SCOUT_VENUES.ca_montreal[type] || [];
  const venue = venues[Math.floor(Math.random() * venues.length)] || venues[0];

  // Date = 1 week from now, rounded to nearest Saturday or weekday evening
  const eventDate = new Date();
  eventDate.setDate(eventDate.getDate() + 7);
  // Push to next Saturday if weekend type
  if (type === 'outdoor' || type === 'apero') {
    const dayOfWeek = eventDate.getDay();
    const daysToSat = (6 - dayOfWeek + 7) % 7 || 7;
    eventDate.setDate(eventDate.getDate() + daysToSat);
  }

  const confirmDeadline = new Date();
  confirmDeadline.setHours(confirmDeadline.getHours() + 48);

  const groupScore = scoreGroup(members);

  return {
    id: id || `scout_${Date.now()}`,
    type,
    status: 'proposed',  // proposed | confirmed | cancelled | past
    network,
    members: members.map(m => ({
      id: m.id,
      name: m.name,
      age: m.age,
      gender: m.gender,
      photo: (m.photos || [])[0] || null,
      confirmed: false,
      voted: false,
      vote: null,  // null | 'yes' | 'no'  (visible only to self post-scout)
    })),
    sharedInterests: groupScore.sharedInterests,
    avgCompat: groupScore.avgCompat,
    venue,
    date: eventDate.toISOString().split('T')[0],
    time: type === 'outdoor' ? '10:00' : type === 'workshop' ? '14:00' : '19:30',
    confirmDeadline: confirmDeadline.toISOString(),
    minConfirmations: 4,
    proposedAt: new Date().toISOString(),
    postScout: null,  // populated after the event
  };
}

// ── Format Scout date for display ───────────────────────────────────────
export function formatScoutDate(dateStr, lang = 'en') {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: 'long', month: 'long', day: 'numeric'
  });
}

export function formatDeadlineCountdown(isoStr) {
  const diff = new Date(isoStr).getTime() - Date.now();
  if (diff <= 0) return '0h';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h${m > 0 ? m + 'm' : ''}` : `${m}m`;
}

// ── Mock Scout data ──────────────────────────────────────────────────────
// DEV FALLBACK — real scouts loaded from Supabase in production
export function getMockScouts(currentUserId = 'me') {
  const now = new Date();
  const oneWeek = new Date(now.getTime() + 7 * 86400000);
  const yesterday = new Date(now.getTime() - 86400000);
  const deadline48 = new Date(now.getTime() + 48 * 3600000);

  return [
    // Active proposal — waiting for confirmation
    {
      id: 'scout_001',
      type: 'dinner',
      status: 'proposed',
      network: 'ca_montreal',
      members: [
        { id: currentUserId, name: 'Toi', age: 28, gender: 'man',    photo: null,  confirmed: false, voted: false, vote: null },
        { id: 'u003', name: 'Sophie',     age: 27, gender: 'woman',  photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&q=80&auto=format&fit=crop', confirmed: true,  voted: false, vote: null },
        { id: 'u007', name: 'Thomas',     age: 30, gender: 'man',    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&q=80&auto=format&fit=crop', confirmed: true,  voted: false, vote: null },
        { id: 'u011', name: 'Camille',    age: 26, gender: 'woman',  photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&q=80&auto=format&fit=crop', confirmed: false, voted: false, vote: null },
        { id: 'u014', name: 'Alex',       age: 29, gender: 'nonbinary', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&q=80&auto=format&fit=crop', confirmed: true, voted: false, vote: null },
        { id: 'u019', name: 'Léa',        age: 28, gender: 'woman',  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&q=80&auto=format&fit=crop', confirmed: false, voted: false, vote: null },
      ],
      sharedInterests: ['interest.coffee', 'interest.cinema', 'interest.reading', 'interest.cooking'],
      avgCompat: 0.74,
      venue: { name: 'Café Resonance', address: '5175 Ave du Parc, Montréal', area: 'Mile-End' },
      date: oneWeek.toISOString().split('T')[0],
      time: '19:30',
      confirmDeadline: deadline48.toISOString(),
      minConfirmations: 4,
      proposedAt: new Date(now.getTime() - 6 * 3600000).toISOString(),
      postScout: null,
    },

    // Past Scout — waiting for post-vote
    {
      id: 'scout_002',
      type: 'outdoor',
      status: 'past',
      network: 'ca_montreal',
      members: [
        { id: currentUserId, name: 'Toi', age: 28, gender: 'man', photo: null, confirmed: true, voted: false, vote: null },
        { id: 'u005', name: 'Marie',   age: 29, gender: 'woman',  photo: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=80&q=80&auto=format&fit=crop', confirmed: true, voted: true, vote: 'yes' },
        { id: 'u009', name: 'Julien',  age: 31, gender: 'man',    photo: 'https://images.unsplash.com/photo-1463453091185-61582044d556?w=80&q=80&auto=format&fit=crop', confirmed: true, voted: true, vote: 'no'  },
        { id: 'u012', name: 'Inès',    age: 27, gender: 'woman',  photo: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=80&q=80&auto=format&fit=crop', confirmed: true, voted: true, vote: 'yes' },
      ],
      sharedInterests: ['interest.hiking', 'interest.coffee', 'interest.photography'],
      avgCompat: 0.81,
      venue: { name: 'Mont-Royal', address: 'Voie Camillien-Houde', area: 'Mont-Royal' },
      date: yesterday.toISOString().split('T')[0],
      time: '10:00',
      confirmDeadline: new Date(now.getTime() - 5 * 86400000).toISOString(),
      minConfirmations: 4,
      proposedAt: new Date(now.getTime() - 10 * 86400000).toISOString(),
      postScout: {
        mutualMatches: ['u005'], // Marie liked back
        revealedScores: {
          u005: { score: 8.4, axes: { narrative:8.1, presence:9.2, generosity:7.8 } },
          u009: { score: 6.1, axes: { narrative:5.8, presence:6.4, generosity:6.0 } },
          u012: { score: 7.7, axes: { narrative:8.0, presence:7.2, generosity:7.9 } },
        },
      },
    },

    // On waitlist
    {
      id: 'scout_waitlist_001',
      type: 'workshop',
      status: 'waiting',
      network: 'ca_montreal',
      members: [],
      sharedInterests: [],
      avgCompat: 0,
      venue: null,
      date: null,
      time: null,
      confirmDeadline: null,
      minConfirmations: 4,
      proposedAt: null,
      waitlistPosition: 3,
      preferences: { type: 'workshop', availability: 'weekend' },
    },
  ];
}

// ── Waitlist preferences storage ─────────────────────────────────────────
export function saveScoutPrefs(prefs) {
  try { localStorage.setItem('beeloveScoutPrefs', JSON.stringify(prefs)); } catch {}
}
export function loadScoutPrefs() {
  try { return JSON.parse(localStorage.getItem('beeloveScoutPrefs') || 'null'); } catch { return null; }
}
